import {
  createContext,
  createElement,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { Permission } from '@/api/permissions';
import type { PermissionSelectionMap } from '@/utils/permission-catalog';
import {
  applyPermissionDraft,
  EMPTY_PERMISSION_DRAFT,
  type PendingPermissions,
  type PermissionDraft,
  prunePermissionDraft,
  toggleInPermissionDraft,
} from '@/utils/permission-draft';

type DraftUpdater = (_current: PermissionDraft) => PermissionDraft;

/** Every role's unsaved draft on one board, keyed by role id. */
export type PermissionDraftStore = {
  drafts: ReadonlyMap<string, PermissionDraft>;
  update: (_roleId: string, _next: DraftUpdater) => void;
};

const EMPTY_DRAFTS: ReadonlyMap<string, PermissionDraft> = new Map();

export const usePermissionDraftStore = (): PermissionDraftStore => {
  const [drafts, setDrafts] = useState(EMPTY_DRAFTS);

  const update = useCallback(
    (roleId: string, next: DraftUpdater) =>
      setDrafts((current) => {
        const previous = current.get(roleId) ?? EMPTY_PERMISSION_DRAFT;
        const value = next(previous);
        if (value === previous) return current;

        const updated = new Map(current);
        if (value.size) updated.set(roleId, value);
        else updated.delete(roleId);

        return updated;
      }),
    [],
  );

  return useMemo(() => ({ drafts, update }), [drafts, update]);
};

const PermissionDraftContext = createContext<PermissionDraftStore | null>(null);

/**
 * Holds the board's drafts above its tabs and views. The permissions panel unmounts whenever
 * the Members or Activity tab, the Compare view or another role is shown, and a draft kept in
 * the panel would go with it.
 */
export const PermissionDraftProvider: FC<{ store: PermissionDraftStore; children: ReactNode }> = ({
  store,
  children,
}) => createElement(PermissionDraftContext.Provider, { value: store }, children);

type PermissionDraftOptions = {
  /** Requests already waiting for a decision; their permissions are never staged. */
  pending?: PendingPermissions;
  /**
   * False until the role's saved permissions have loaded. Pruning against an empty selection
   * would read every staged removal as already applied and drop it.
   */
  ready?: boolean;
};

/**
 * Staged permission edits for the role on screen. Switches change the draft, not the role; the
 * board submits the draft as a batch after the review step. Pruned against the saved rows so a
 * change that has since landed drops out, and against pending requests so a change that can no
 * longer be sent cannot get stuck.
 */
export const usePermissionDraft = (
  roleId: string,
  saved: PermissionSelectionMap,
  { pending, ready = true }: PermissionDraftOptions = {},
) => {
  const ownStore = usePermissionDraftStore();
  const store = useContext(PermissionDraftContext) ?? ownStore;
  const { update: updateStore } = store;

  const stored = store.drafts.get(roleId) ?? EMPTY_PERMISSION_DRAFT;
  const draft = useMemo(
    () => (ready ? prunePermissionDraft(stored, saved, pending) : stored),
    [ready, stored, saved, pending],
  );

  // Writes the pruned draft back so the board's unsaved-changes check only counts what is still
  // to be sent.
  useEffect(() => {
    if (draft !== stored) updateStore(roleId, () => draft);
  }, [draft, stored, roleId, updateStore]);

  const update = useCallback(
    (next: DraftUpdater) => updateStore(roleId, next),
    [roleId, updateStore],
  );

  const toggle = useCallback(
    (permission: Permission, granted: boolean) =>
      update((current) => toggleInPermissionDraft(current, saved, permission, granted, pending)),
    [saved, pending, update],
  );

  const stageMany = useCallback(
    (add: readonly Permission[], remove: readonly Permission[]) =>
      update((current) => {
        const withAdds = add.reduce(
          (next, permission) => toggleInPermissionDraft(next, saved, permission, true, pending),
          current,
        );

        return remove.reduce(
          (next, permission) => toggleInPermissionDraft(next, saved, permission, false, pending),
          withAdds,
        );
      }),
    [saved, pending, update],
  );

  /** Keeps only the given permissions staged, e.g. the ones whose request failed. */
  const keepOnly = useCallback(
    (permissions: ReadonlySet<Permission>) =>
      update(
        (current) => new Map([...current].filter(([permission]) => permissions.has(permission))),
      ),
    [update],
  );

  const discard = useCallback(() => update(() => EMPTY_PERMISSION_DRAFT), [update]);

  const effective = useMemo(() => applyPermissionDraft(saved, draft), [saved, draft]);

  return { draft, effective, toggle, stageMany, keepOnly, discard };
};
